// order_created ("checkout opened") for the paywall-popup flow — the popup's
// equivalent of the regular paywall's checkout-opened event (per the ticket: "when
// user opened checkout"). Fired right BEFORE the redirect to the Upgate hosted page,
// because subscription_purchase happens post-redirect on the platform and cannot be
// emitted from the LP.
//
// Shared by both orders of the flow, which open checkout from different places:
//   after_signup — PaywallPopup.handleContinue (the popup is post-registration, so
//                  the tap on Continue IS the checkout call);
//   popup_first  — createAccount, after registration mints the authToken (the tap on
//                  Continue happened earlier, while still anonymous — it emits
//                  paywall_popup_plan_selected instead, see utils/analytics.js).
// Keeping it in one place is what guarantees the two arms stay comparable in GA4 /
// Mixpanel: same event name, same props, same three sinks.
import { trackOrderCreated } from '../../utils/analytics';
import {
  trackServerOrderCreated,
  getRegistrationAttribution,
} from '../../utils/serverGtm';
import { trackOrderCreatedMixpanel } from '../../utils/mixpanel';

// plan: the subscription plan object; currencyCode: ISO code for analytics
// (`symbol` stays display-only); userId: Mongo _id (Mixpanel identity — the send is
// skipped without it); email + clickIds: the same attribution the registration
// events carry.
export const trackPopupOrderCreated = ({
  plan,
  currencyCode,
  paywallType,
  paywallOrder,
  userId,
  email,
  clickIds,
}) => {
  if (!plan?._id) return;

  const durationLabel = `${plan.duration_months} ${
    plan.duration_months > 1 ? 'months' : 'month'
  }`;
  const orderItem = {
    item_id: plan._id,
    item_name: `${durationLabel} subscription`,
    item_category: 'subscriptions',
    price: Number(plan.paymentAmount),
    currency: currencyCode,
  };

  // Client dataLayer (LP container) — ISO code + experiment variant. `value`
  // coerced to a number so it matches the sGTM item.price type.
  trackOrderCreated({
    plan_id: plan._id,
    value: Number(plan.paymentAmount),
    currency: currencyCode,
    duration_months: plan.duration_months,
    'paywall-type': paywallType,
    'paywall-order': paywallOrder,
  });

  // Server-side GTM (sGTM), mirroring the platform's trackGTMOrderCreated.
  // email + clickIds are threaded from the call site so the event carries the same
  // rich attribution as the platform's backend-enriched GTM events.
  trackServerOrderCreated({
    item: orderItem,
    paywallType,
    paywallOrder,
    email,
    clickIds,
  });

  // Mixpanel order_created — the backend/platform don't send this event to
  // Mixpanel, so the LP is the only source. Identified by the Mongo _id so it sits
  // on the user's profile and powers the registration_started → order_created
  // funnel. NOT deduped (a user may reopen checkout); callers guard against
  // re-fires within a session. Carries the same email + attribution as the
  // registration events (as EVENT props — the profile already holds $email/UTM from
  // registration, so we don't re-write it here).
  trackOrderCreatedMixpanel(userId, {
    plan_id: plan._id,
    value: Number(plan.paymentAmount),
    currency: currencyCode,
    duration_months: plan.duration_months,
    'paywall-type': paywallType,
    'paywall-order': paywallOrder,
    ...(email ? { email } : {}),
    ...getRegistrationAttribution(),
  });
};

export default { trackPopupOrderCreated };
